import {
  getAllOrgConfigs,
  getClientOrgConfig,
} from '@provider-directory/app/lib/client-org-config';
import { resolveAssociationPhoneSelection } from '@provider-directory/functions/resolve-association-phone-selection';
import { resolveEnvScopedUrl } from '@provider-directory/functions/resolve-env-scoped-url';
import {
  Environment,
  getDeploymentEnvironment,
} from '@provider-directory/services/getEnvironmentVariables';
import type {
  ClientUIConfig,
  LocalDirectoryConfig,
  TranslatableProviderField,
} from '@provider-directory/types';
import type { FeatureFlags } from '@provider-directory/types/flags';
import {
  ContextKeySchema,
  ClientOrgContextKey,
} from '@provider-directory/types/organizations.contracts';
import type { ProviderResource } from '@provider-directory/types/providers.contracts';

/** The only part of a provider MyChart resolution depends on. */
type ProviderHospitalAssociations = ProviderResource['hospitalAssociations'];
import type {
  AssociationPhoneSelection,
  MyChartConfig,
  RuntimeConfig,
} from '@provider-directory/types/runtime-config.contracts';

/**
 * Already-resolved MyChart visit-type inputs handed to consumers. Call sites
 * receive this and never inspect the tenant key, feature flag, or runtime
 * config themselves.
 */
export interface MyChartVisitTypeResolution {
  url: string;
  /**
   * MyChart sign-in deep link used by the decision-tree error flows. Optional
   * and resolved independently of `url`: a directory may offer scheduling
   * without a sign-in link, and an absent one makes only that CTA inert.
   */
  loginUrl?: string;
  defaultVisitTypeId: string;
  specialtyToVisitTypeId: Partial<Record<string, string>>;
}

// Resolution runs per provider card on every render, so warn at most once per
// directory. Only a MISCONFIGURED block warns — a directory that simply has no
// MyChart is a valid, silent state.
const warnedUnresolvableMyChart = new Set<ClientOrgContextKey>();

const warnedUnresolvableFindDoctorUrl = new Set<ClientOrgContextKey>();

/** Flag OFF → local URL; flag ON → resolve runtime map (no local fallback). */
const resolveFindDoctorUrl = (
  key: ClientOrgContextKey,
  localFindDoctorUrl: string | undefined,
  runtimeFindDoctorUrl: RuntimeConfig['findDoctorUrl'],
  isRolloutEnabled: boolean | undefined,
): string | undefined => {
  if (!isRolloutEnabled) {
    return localFindDoctorUrl;
  }
  if (runtimeFindDoctorUrl === undefined) {
    return undefined;
  }
  const resolved = resolveEnvScopedUrl(
    runtimeFindDoctorUrl,
    getDeploymentEnvironment(),
  );
  if (resolved === undefined && !warnedUnresolvableFindDoctorUrl.has(key)) {
    warnedUnresolvableFindDoctorUrl.add(key);
    console.warn(
      '[OrgContext] findDoctorUrl is present but has no URL for this environment',
    );
  }
  return resolved;
};

/**
 * Merges local + runtime config. Phase 1 fields are gated by
 * `isConfigRolloutGroupEnabled` (OFF → local, ON → runtime only).
 */
const assembleConfig = (
  key: ClientOrgContextKey,
  localConfig: LocalDirectoryConfig,
  runtimeConfig: RuntimeConfig,
  featureFlags: FeatureFlags,
): ClientUIConfig => {
  const isRolloutEnabled = featureFlags.isConfigRolloutGroupEnabled;
  return {
    ...localConfig,
    name: runtimeConfig.name,
    customer: runtimeConfig.customer,
    customerId: runtimeConfig.customerId,
    companyUrl: runtimeConfig.companyUrl,
    // `parentDirectory` is validated only as `string | null` at the runtime
    // boundary, so re-validate it against the closed key set here and treat
    // any unexpected value as absent rather than casting blindly.
    parentDirectory: ContextKeySchema.safeParse(runtimeConfig.parentDirectory)
      .data,
    // Absent in the runtime payload means "this directory offers no parent
    // redirect". There is no local value left to fall back to.
    subdirectoryEmptyRedirect: runtimeConfig.subdirectoryEmptyRedirect,
    branding: {
      ...localConfig.branding,
      logoWidth: runtimeConfig.branding.logoWidth,
      logoHeight: runtimeConfig.branding.logoHeight,
      color: runtimeConfig.branding.color,
      // Taken whole: the runtime contract requires every shade (see
      // `paletteSchema`). `...localConfig.branding` above carries the local
      // half — logoUrl / cssVariables / fonts.
      palette: runtimeConfig.branding.palette,
    },
    scheduling: {
      phoneNumber: runtimeConfig.scheduling.phoneNumber,
      displayNumber: runtimeConfig.scheduling.displayNumber,
      eventDuration: runtimeConfig.scheduling.eventDuration,
    },
    primaryCareKeywords: runtimeConfig.primaryCareKeywords,
    legal: isRolloutEnabled ? runtimeConfig.legal : localConfig.legal,
    sitemap: isRolloutEnabled
      ? { excludeProvidersPath: runtimeConfig.sitemap?.excludeProvidersPath }
      : localConfig.sitemap,
    appointmentTypes: isRolloutEnabled
      ? runtimeConfig.appointmentTypes
      : localConfig.appointmentTypes,
    hasOwnFindDoctorPage: isRolloutEnabled
      ? runtimeConfig.hasOwnFindDoctorPage
      : localConfig.hasOwnFindDoctorPage,
    findDoctorUrl: resolveFindDoctorUrl(
      key,
      localConfig.findDoctorUrl,
      runtimeConfig.findDoctorUrl,
      isRolloutEnabled,
    ),
    translation: isRolloutEnabled
      ? runtimeConfig.translationFields && {
          ...runtimeConfig.translationFields,
          enabledFields: runtimeConfig.translationFields
            .enabledFields as TranslatableProviderField[],
        }
      : localConfig.translation,
  };
};

export default class OrgContextModel {
  public readonly key: ClientOrgContextKey;
  public readonly config: ClientUIConfig;
  /**
   * The LOCAL half for every directory. Only the active directory can be fully
   * assembled — the API answers for one directory at a time — so this map
   * deliberately carries local fields only, and `config` is the assembled one.
   */
  public readonly allConfigs: Record<ClientOrgContextKey, LocalDirectoryConfig>;
  // Kept so SSR can forward the original runtime payload to the client-side
  // OrgContextProvider, which reconstructs the model in the browser and
  // would otherwise lose the mixed-source assembly.
  public readonly runtimeConfig: RuntimeConfig;

  constructor(
    key: ClientOrgContextKey,
    featureFlags: FeatureFlags = {},
    runtimeConfig: RuntimeConfig,
  ) {
    this.key = key;
    this.runtimeConfig = runtimeConfig;
    this.allConfigs = getAllOrgConfigs(featureFlags);
    const localConfig = getClientOrgConfig(key, featureFlags);
    this.config = assembleConfig(key, localConfig, runtimeConfig, featureFlags);
  }

  /** Thin accessor over the pure resolver. See resolve-association-phone-selection. */
  getAssociationPhoneSelection = (): AssociationPhoneSelection =>
    resolveAssociationPhoneSelection({ runtimeConfig: this.runtimeConfig });

  /**
   * Resolves the MyChart visit-type inputs for a provider without exposing any
   * tenant identity to the caller.
   *
   * `undefined` means **this directory has no MyChart** — callers render nothing
   * and clicking does nothing. It is a normal state, not an error: a directory
   * opts into MyChart purely by carrying `integration.myChart` (see `acme`).
   */
  getMyChartVisitTypeConfig = (
    provider: ProviderResource,
  ): MyChartVisitTypeResolution | undefined =>
    this.resolveMyChart(provider.hospitalAssociations);

  /**
   * MyChart sign-in deep link for the ACTIVE directory, resolved through the
   * same association-override path as the scheduling URL so the two can never
   * disagree. `provider` is optional because the decision-tree error scenes can
   * render before a provider is loaded; with no associations no override
   * matches, which yields the directory's own login URL.
   *
   * Before DIR-3583 the call sites read `config.myChart.loginUrl` off
   * `useOrgContextBasedOnHospitalAssociations(...)` — i.e. a *sibling*
   * directory's config. This keeps the read self-contained.
   */
  getMyChartLoginUrl = (provider?: ProviderResource): string | undefined =>
    this.resolveMyChart(provider?.hospitalAssociations ?? [])?.loginUrl;

  /**
   * The runtime config is the only source. A directory without a `myChart`
   * block, or one whose URLs do not resolve for this environment, simply has no
   * MyChart — there is no local fallback.
   */
  private resolveMyChart = (
    hospitalAssociations: ProviderHospitalAssociations,
  ): MyChartVisitTypeResolution | undefined => {
    if (this.runtimeConfig.myChart === undefined) {
      return undefined;
    }
    const resolved = resolveRuntimeMyChart(
      this.runtimeConfig.myChart,
      hospitalAssociations,
      getDeploymentEnvironment(),
    );
    if (resolved === null && !warnedUnresolvableMyChart.has(this.key)) {
      // Distinct from "no myChart at all": the block exists but carries no
      // entry this environment can use (e.g. no `default` and no `local`),
      // which is a seeding gap rather than an opted-out directory.
      warnedUnresolvableMyChart.add(this.key);
      console.warn(
        '[OrgContext] integration.myChart is present but has no URL for this environment; MyChart is unavailable here',
      );
    }
    return resolved ?? undefined;
  };

  getProvidersUrl = (baseURL = ''): string => {
    return this.config.findDoctorUrl ?? `${baseURL}/providers`;
  };
}

/**
 * Backend-owned resolution. Uses the runtime `integration.myChart` as the
 * source of truth. A `hospitalAssociationOverrides` entry applies when every
 * hospital association on the provider matches that entry's key (e.g. a WPH-only
 * provider browsed under a Montefiore parent), replacing the tenant-name check
 * with declarative data.
 *
 * `url` and `loginUrl` arrive as per-environment maps and are collapsed to plain
 * strings here, so every consumer keeps seeing scalars.
 *
 * Returns `null` when the scheduling `url` has no entry for this environment —
 * there is nothing to open, so the directory has no MyChart here. `loginUrl` is
 * resolved independently: absent simply leaves that one CTA inert.
 */
const resolveRuntimeMyChart = (
  myChart: MyChartConfig,
  associations: ProviderHospitalAssociations,
  env: Environment,
): MyChartVisitTypeResolution | null => {
  const url = resolveEnvScopedUrl(myChart.url, env);
  if (url === undefined) {
    return null;
  }

  const loginUrl =
    myChart.loginUrl !== undefined
      ? resolveEnvScopedUrl(myChart.loginUrl, env)
      : undefined;

  if (!myChart.enabled) {
    return {
      url,
      loginUrl,
      defaultVisitTypeId: myChart.defaultVisitTypeId,
      specialtyToVisitTypeId: {},
    };
  }

  const overrides = myChart.hospitalAssociationOverrides ?? {};
  const overrideKey = Object.keys(overrides).find(
    (code) =>
      associations.length > 0 && associations.every((h) => h.code === code),
  );
  const override =
    overrideKey !== undefined ? overrides[overrideKey] : undefined;

  return {
    url,
    // A sub-entity only carries its own login URL when it uses a different
    // MyChart instance; otherwise it inherits the directory's.
    loginUrl:
      (override?.loginUrl !== undefined
        ? resolveEnvScopedUrl(override.loginUrl, env)
        : undefined) ?? loginUrl,
    defaultVisitTypeId:
      override?.defaultVisitTypeId ?? myChart.defaultVisitTypeId,
    specialtyToVisitTypeId:
      override?.specialtyToVisitTypeId ?? myChart.specialtyToVisitTypeId,
  };
};

export interface OrgContextModelApiResponse
  extends Pick<OrgContextModel, 'key' | 'config' | 'runtimeConfig'> {}
